import { Component, OnInit, TemplateRef, inject } from '@angular/core';
import { AuthenticationService, reRgType } from 'app/services/auth.service';
import { APIService, portfolioApiDataType, pgfActivityType } from 'app/services/api.service';
import { AppConfig } from "app/app.config";
import { LoaderService } from "app/services/loader.service";
import { NgbModal, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { AdvisorContactComponent } from "app/modules/shared/advisor-contact.component";
import { MenuComponent } from '../../components/Controls/menu/menu.component';
import { NWMDoughnutChartComponent } from '../../components/NWMCharts/nwmdoughnutchart.component';
import { RouterLink } from '@angular/router';
import { PGFChartComponent } from '../../components/NWMCharts/pgfchart.component';
import { NgClass, DecimalPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { AdvisorContactComponent as AdvisorContactComponent_1 } from '../shared/advisor-contact.component';
import { MinusSignToParens } from 'app/pipes/minus-sign-to-parens.pipe';

type charityType = {
  CharityId: number;
  CharityName: string;
  CharityType: string;
  AmountCAD: number;
  Percent: number;
}

type doughnutChartData = {
  Label: string[];
  BackgroundColor: string[];
  Data: number[];
  Link: string[];
}

@Component({
    templateUrl: './pgf.component.html',
    styleUrls: ['./pgf.component.scss'],
    imports: [MenuComponent, NWMDoughnutChartComponent, RouterLink, NgbTooltip, PGFChartComponent, NgClass, AdvisorContactComponent_1, MinusSignToParens, DecimalPipe, CurrencyPipe, DatePipe]
})

export class PgfComponent implements OnInit {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private loaderService = inject(LoaderService);
  config = inject(AppConfig);
  modalService = inject(NgbModal);

  public selectedReRg?: reRgType;
  public pgfActivity: pgfActivityType[] = [];
  public valuationDate = '';
  public inceptionDate = '';
  public modalTitle = '';
  public modalAction: 'PgfDonation' | 'PgfGrant' | 'MessageAdvisor' = 'PgfGrant';
  public totalGrants = 0;
  public portfolioTotal = 0;
  public pgfName = '';

  /*** Your Charitable Impact ***/
  public chImpactColors = ['#175155','#94B1C2','#7D2A90','#3BA18C','#A34A9C','#756BB1','#005B70','#C6DAE8'];
  public grantsPerCharity:charityType[] = [];
  public charities2show = 7;
  public otherCharityAmount = 0;
  public otherCharityPercent = 100;
  /*** /Your Charitable Impact ***/

  /*** Calendar Year Donation to Funds & Grants to Charities ***/
  public years2show = 10;
  public chartLabels:string[] = [];
  public yearDonations:number[] = [];
  public yearGrants:number[] = [];
  /*** /Calendar Year Donation to Funds & Grants to Charities ***/

  /*** Area of Impact ***/
  public grantsPerAreaImpact: charityType[] = [];
  public AreaImpact2show = 4;
  public otherCharityAreaImpactAmount = 0;
  public otherCharityAreaImpactPercent = 100;
  public areaImpactChartData: doughnutChartData = {
    Label: [],
    BackgroundColor: [],
    Data: [],
    Link: []
  };
  public chAreaImpactColors = ['#A34A9C','#3BA18C','#756BB1','#94B1C2','#175155','#7D2A90', '#005B70','#C6DAE8' ];
  public otherImpactAreaTitle = 'Other Impact Areas';
  /*** Area of Impact ***/

  /*** Most Recent Transactions ***/
  public recentTransactions: pgfActivityType[] = [];

  ngOnInit() {
    this.selectedReRg = this.auth.getCurrentReRg()!;
    this.valuationDate = this.auth.getCurrentValuationDate();
    this.loaderService.display(true, 'pgf-welcome');
    this.pgfName = this.selectedReRg.ClientName.replace(', PGF','');

    this.api.get<portfolioApiDataType>(`${this.config.portfolio.portfolioSummaryUrl}/${this.selectedReRg.ReRgId}/${this.valuationDate}`).subscribe(
      data => {
        this.portfolioTotal = data.total;
        this.inceptionDate = data.inceptionDate;
      }
    );

    this.api.getPgfActivityByReRgId(this.selectedReRg!.ReRgId).subscribe({
      next: data => {
         this.pgfActivity = Array.isArray(data) ? data.sort((a,b)=>a.ActivityDate>b.ActivityDate?1:-1) : [];
        const year = (new Date()).getFullYear();
        this.chartLabels = Array.from({length:this.years2show}, (v,i)=>''+(year-this.years2show + i + 1));
        this.yearGrants = Array.from({length:this.years2show},()=>0);
        this.yearDonations = Array.from({length:this.years2show},()=>0);

        let minChartIndex = this.years2show;
        this.pgfActivity.forEach(a=>{
          if (a.ActivityType === 'Grant') {
            this.totalGrants+= a.AmountCAD;
            const charity = this.grantsPerCharity.find(g=>g.CharityId === a.CharityId);
            if (charity) {
              charity.AmountCAD += a.AmountCAD;
            } else {
              this.grantsPerCharity.push({
                CharityId: a.CharityId,
                CharityName: a.CharityName,
                CharityType: a.CharityType,
                AmountCAD: a.AmountCAD,
                Percent: 0
              });
            }
          }

          /*** Calendar Year Donation to Funds & Grants to Charities ***/
          const activityYear = +a.ActivityDate.split('-')[0];
          const index = this.years2show - (year - activityYear) - 1;
          if (index>=0 && index<=this.years2show) {
            if (index < minChartIndex) minChartIndex = index;
            if (a.ActivityType === 'Grant') this.yearGrants[index]+=a.AmountCAD;
            if (a.ActivityType === 'Donation') this.yearDonations[index]+=a.AmountCAD;
          }
          /*** /Calendar Year Donation to Funds & Grants to Charities ***/

          /*** Area of Impact ***/
          if (a.ActivityType === 'Grant') {
            if (a.CharityType === '' || a.CharityType === 'Other') {
              this.otherCharityAreaImpactAmount += a.AmountCAD;
            } else {
              const charityType = this.grantsPerAreaImpact.find(g => g.CharityType === a.CharityType);
              if (charityType) {
                charityType.AmountCAD += a.AmountCAD;
              } else {
                this.grantsPerAreaImpact.push({
                  CharityId: a.CharityId,
                  CharityName: a.CharityName,
                  CharityType: a.CharityType,
                  AmountCAD: a.AmountCAD,
                  Percent: 0
                });
              }
            }
          }
          /** Area of Impact ***/
        });

        if (minChartIndex > 0) {
          this.yearGrants = this.yearGrants.slice(minChartIndex);
          this.yearDonations = this.yearDonations.slice(minChartIndex);
          this.chartLabels = this.chartLabels.slice(minChartIndex);
        }

        /*** Your Charitable Impact ***/
        this.grantsPerCharity.sort((a,b)=>a.AmountCAD>b.AmountCAD?-1:1);
        if (this.grantsPerCharity.length >= this.charities2show ) {
          this.otherCharityAmount = Math.round(this.totalGrants);
          this.otherCharityPercent = 100;
          this.grantsPerCharity = this.grantsPerCharity.slice(0,this.charities2show);
        } else {
          this.otherCharityAmount = 0;
          this.otherCharityPercent = 0;
        }

        this.grantsPerCharity.forEach(g=>{
          g.Percent = Math.round(g.AmountCAD*10000/this.totalGrants)/100;
          g.AmountCAD = Math.round(g.AmountCAD);
          if (this.otherCharityPercent>0) {
            this.otherCharityAmount -= g.AmountCAD;
            this.otherCharityPercent -= g.Percent;
          }
        });
        /*** /Your Charitable Impact ***/

        /*** Your Charitable Area Impact ***/
        this.grantsPerAreaImpact.sort((a,b)=>a.AmountCAD>b.AmountCAD?-1:1);
        this.otherCharityAreaImpactAmount = Math.round(this.totalGrants);
        this.otherCharityAreaImpactPercent = 100;
        this.grantsPerAreaImpact = this.grantsPerAreaImpact.slice(0,this.AreaImpact2show);

        this.grantsPerAreaImpact.forEach(g=>{
          g.Percent = Math.round(g.AmountCAD*10000/this.totalGrants)/100;
          g.AmountCAD = Math.round(g.AmountCAD);
          if (this.otherCharityAreaImpactPercent>0) {
            this.otherCharityAreaImpactAmount -= g.AmountCAD;
            this.otherCharityAreaImpactPercent -= g.Percent;
          }
        });
        if (this.otherCharityAreaImpactAmount == 0) {
          this.otherCharityAreaImpactPercent = 0;
        }
        /*** /Your Charitable Area Impact ***/

        /*** build Charitable Area Impact Chart Data **/
        this.grantsPerAreaImpact.forEach(g => {
          this.areaImpactChartData.Data.push(g.AmountCAD);
          this.areaImpactChartData.Label.push(g.CharityType);
          this.areaImpactChartData.Link.push('/pgf/impact/' + encodeURIComponent(g.CharityType));
        });
        if (this.otherCharityAreaImpactPercent>0) {
          this.areaImpactChartData.Data.push(this.otherCharityAreaImpactAmount);
          this.areaImpactChartData.Label.push(this.otherImpactAreaTitle);
          this.areaImpactChartData.Link.push('/pgf/impact/other-impact');
        }
        this.areaImpactChartData.BackgroundColor = this.chAreaImpactColors;
        /*** build Charitable Area Impact Chart Data **/

        /*** Most Recent Transactions ***/
        this.recentTransactions = this.pgfActivity.slice(-3);
        /*** /Most Recent Transactions ***/

        this.loaderService.display(false, 'pgf-welcome');
      },
      error: () => {
        this.loaderService.display(false, 'pgf-welcome');
      }
    });
  }

  openModalToContactAdvisor(content: TemplateRef<AdvisorContactComponent>, type: 'PgfDonation'|'PgfGrant'|'MessageAdvisor') {
    this.modalTitle = type == 'MessageAdvisor' ? 'Contact Advisory Team':
      type == 'PgfDonation' ? 'Request to Donate to Fund' :
      'Request to Send Grant to Charity';
    this.modalAction = type;
    this.modalService.open(content, { size: 'lg' })
  }

  encodeUriComponent(value: string): string {
    return encodeURIComponent(value);
  }
}
